"use client";

import { useState } from "react";
import { X, Briefcase, CheckCircle2, ArrowRight, Loader2, Sparkles } from "lucide-react";
import { EXECUTIVE_PRACTICES, calculatePlacementFee } from "@/lib/demos/hiringData";

interface RequisitionProposalModalProps {
  isOpen: boolean;
  onClose: () => void;
  currency: "INR" | "USD";
  initialPracticeId?: string;
  initialCtc?: number;
  onSuccess?: () => void;
}

export function RequisitionProposalModal({
  isOpen,
  onClose,
  currency,
  initialPracticeId = "c-suite",
  initialCtc,
  onSuccess,
}: RequisitionProposalModalProps) {
  const [practiceId, setPracticeId] = useState(initialPracticeId);
  const selectedPractice =
    EXECUTIVE_PRACTICES.find((p) => p.id === practiceId) || EXECUTIVE_PRACTICES[0];

  const bounds =
    currency === "USD"
      ? selectedPractice.rolesBenchmarkUsd
      : selectedPractice.rolesBenchmarkInr;

  const [ctc, setCtc] = useState<number>(initialCtc || bounds.default);
  const [clientCompany, setClientCompany] = useState("");
  const [candidateName, setCandidateName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [targetRole, setTargetRole] = useState(selectedPractice.typicalRoles[0]);
  const [currentTitle, setCurrentTitle] = useState("");
  const [yearsExperience, setYearsExperience] = useState(14);
  const [location, setLocation] = useState("Bangalore, India");
  const [notes, setNotes] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedCandidate, setSubmittedCandidate] = useState<any>(null);
  const [errorMsg, setErrorMsg] = useState("");

  if (!isOpen) return null;

  const feeCalc = calculatePlacementFee(practiceId, ctc, currency);
  const sym = currency === "USD" ? "$" : "₹";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientCompany || !contactEmail || !candidateName || !targetRole) {
      setErrorMsg("Please fill in all mandatory mandate fields.");
      return;
    }

    setIsSubmitting(true);
    setErrorMsg("");

    try {
      const res = await fetch("/api/demos/hiring/candidates", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client_company: clientCompany,
          candidate_name: candidateName,
          email: contactEmail,
          phone,
          target_role: targetRole,
          current_title: currentTitle || `Senior Leader (${targetRole})`,
          practice_id: practiceId,
          practice_name: selectedPractice.title,
          years_experience: yearsExperience,
          current_company: "Confidential / Active Employment",
          location,
          expected_ctc: ctc,
          currency,
          placement_fee: feeCalc.placementFee,
          skills_tags: JSON.stringify(["Executive Leadership", "Strategic Vision", "Org Scaling"]),
          notes: notes || "Mandate registered via online executive search portal.",
        }),
      });

      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to register executive requisition.");
      }

      setSubmittedCandidate(data.candidate);
      if (onSuccess) onSuccess();
    } catch (err: any) {
      setErrorMsg(err.message || "An unexpected error occurred.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-2xl bg-[#140c21] border border-purple-800/50 rounded-2xl shadow-2xl overflow-hidden text-white">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-purple-900/40 bg-[#170f2b]">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center border border-purple-500/30">
              <Briefcase className="w-4 h-4 text-purple-400" />
            </div>
            <div>
              <h3 className="font-bold text-white text-base">
                Initiate Executive Search Mandate
              </h3>
              <p className="text-[11px] text-purple-300">
                Nexus Executive Search • Retained Mandate Intake
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-purple-900/40 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 max-h-[80vh] overflow-y-auto">
          {submittedCandidate ? (
            <div className="text-center py-6 space-y-5">
              <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto text-emerald-400">
                <CheckCircle2 className="w-8 h-8" />
              </div>

              <div>
                <h4 className="text-xl font-extrabold text-white">
                  Executive Mandate Registered!
                </h4>
                <p className="text-sm text-slate-300 max-w-md mx-auto mt-1">
                  Mandate Reference <span className="font-mono text-purple-300 font-bold">{submittedCandidate.candidate_no}</span> has been ingested into our Executive Search ATS. A formal proposal notification has been dispatched to <span className="text-white font-medium">{submittedCandidate.email}</span>.
                </p>
              </div>

              <div className="p-4 rounded-xl bg-[#1a112c] border border-purple-900/50 max-w-md mx-auto text-left text-xs space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-400">Target Role:</span>
                  <span className="font-bold text-white">{submittedCandidate.target_role}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Hiring Organization:</span>
                  <span className="font-bold text-white">{submittedCandidate.client_company}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Practice Area:</span>
                  <span className="text-purple-300 font-semibold">{submittedCandidate.practice_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Target Package (CTC):</span>
                  <span className="font-bold text-emerald-400">{sym}{submittedCandidate.expected_ctc.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Search Partner:</span>
                  <span className="text-white">{submittedCandidate.assigned_recruiter}</span>
                </div>
              </div>

              <div className="pt-3 flex gap-3 justify-center">
                <a
                  href="/demos/hiring/crm"
                  className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white text-xs font-bold flex items-center gap-2 shadow-lg shadow-purple-600/30"
                >
                  <span>Open Recruitment Pipeline CRM</span>
                  <ArrowRight className="w-4 h-4" />
                </a>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-5 py-2.5 rounded-xl bg-purple-950/60 hover:bg-purple-900 text-slate-300 hover:text-white border border-purple-800/40 text-xs font-semibold"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 text-xs">
              {errorMsg && (
                <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs">
                  {errorMsg}
                </div>
              )}

              {/* Practice & Role */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-300 font-medium mb-1">
                    Search Practice Area *
                  </label>
                  <select
                    value={practiceId}
                    onChange={(e) => {
                      setPracticeId(e.target.value);
                      const p = EXECUTIVE_PRACTICES.find((x) => x.id === e.target.value);
                      if (p) {
                        setTargetRole(p.typicalRoles[0]);
                        setCtc(currency === "USD" ? p.rolesBenchmarkUsd.default : p.rolesBenchmarkInr.default);
                      }
                    }}
                    className="w-full bg-[#1c1230] border border-purple-900/60 rounded-xl px-3 py-2.5 text-white focus:outline-none focus:border-purple-500"
                  >
                    {EXECUTIVE_PRACTICES.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.title} ({p.baseFeePercentage}% Fee)
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-slate-300 font-medium mb-1">
                    Target Appointment Role *
                  </label>
                  <input
                    type="text"
                    required
                    value={targetRole}
                    onChange={(e) => setTargetRole(e.target.value)}
                    placeholder="e.g. Chief Technology Officer"
                    className="w-full bg-[#1c1230] border border-purple-900/60 rounded-xl px-3 py-2.5 text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>

              {/* Client Organization & Contact */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-300 font-medium mb-1">
                    Hiring Organization / Client Entity *
                  </label>
                  <input
                    type="text"
                    required
                    value={clientCompany}
                    onChange={(e) => setClientCompany(e.target.value)}
                    placeholder="e.g. HyperScale Cloud Systems"
                    className="w-full bg-[#1c1230] border border-purple-900/60 rounded-xl px-3 py-2.5 text-white focus:outline-none focus:border-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 font-medium mb-1">
                    Candidate / Prospect Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={candidateName}
                    onChange={(e) => setCandidateName(e.target.value)}
                    placeholder="e.g. Dr. Arvind Nambiar"
                    className="w-full bg-[#1c1230] border border-purple-900/60 rounded-xl px-3 py-2.5 text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>

              {/* Email & Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-300 font-medium mb-1">
                    Hiring Authority / Candidate Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="e.g. decisionmaker@company.com"
                    className="w-full bg-[#1c1230] border border-purple-900/60 rounded-xl px-3 py-2.5 text-white focus:outline-none focus:border-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 font-medium mb-1">
                    Phone / WhatsApp Contact
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+91 98801 00000"
                    className="w-full bg-[#1c1230] border border-purple-900/60 rounded-xl px-3 py-2.5 text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>

              {/* Experience & Location */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-slate-300 font-medium mb-1">
                    Target CTC ({currency}) *
                  </label>
                  <input
                    type="number"
                    required
                    value={ctc}
                    onChange={(e) => setCtc(Number(e.target.value))}
                    className="w-full bg-[#1c1230] border border-purple-900/60 rounded-xl px-3 py-2.5 text-white focus:outline-none focus:border-purple-500 font-mono"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 font-medium mb-1">
                    Total Experience (Years)
                  </label>
                  <input
                    type="number"
                    value={yearsExperience}
                    onChange={(e) => setYearsExperience(Number(e.target.value))}
                    className="w-full bg-[#1c1230] border border-purple-900/60 rounded-xl px-3 py-2.5 text-white focus:outline-none focus:border-purple-500 font-mono"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 font-medium mb-1">
                    Work Location / Mode
                  </label>
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g. Bangalore / Hybrid"
                    className="w-full bg-[#1c1230] border border-purple-900/60 rounded-xl px-3 py-2.5 text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>

              {/* Current Title & Notes */}
              <div>
                <label className="block text-slate-300 font-medium mb-1">
                  Current Executive Designation / Organization
                </label>
                <input
                  type="text"
                  value={currentTitle}
                  onChange={(e) => setCurrentTitle(e.target.value)}
                  placeholder="e.g. VP of Platform Engineering at Cognitive Cloud Labs"
                  className="w-full bg-[#1c1230] border border-purple-900/60 rounded-xl px-3 py-2.5 text-white focus:outline-none focus:border-purple-500"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-medium mb-1">
                  Confidential Search Requirements &amp; Notes
                </label>
                <textarea
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. Board presentation required; experience scaling distributed systems to 10M+ DAUs preferred."
                  className="w-full bg-[#1c1230] border border-purple-900/60 rounded-xl px-3 py-2 text-white focus:outline-none focus:border-purple-500"
                />
              </div>

              {/* Real-time Summary Box */}
              <div className="p-3.5 rounded-xl bg-[#181028] border border-purple-900/40 flex justify-between items-center">
                <div>
                  <div className="text-[10px] text-slate-400">Success Fee ({selectedPractice.baseFeePercentage}%):</div>
                  <div className="text-xs font-bold text-white">
                    {sym}{feeCalc.placementFee.toLocaleString()} + 18% Tax
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-emerald-400 font-semibold">
                    {selectedPractice.guaranteeDays}-Day Replacement Guarantee
                  </div>
                  <div className="text-xs font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-300 to-indigo-300">
                    Total: {sym}{feeCalc.totalInvoiceAmount.toLocaleString()}
                  </div>
                </div>
              </div>

              <div className="pt-2 flex justify-end gap-2.5">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2.5 rounded-xl bg-purple-950/60 hover:bg-purple-900 text-slate-400 hover:text-white text-xs font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 via-fuchsia-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold text-xs shadow-lg shadow-purple-600/30 flex items-center gap-2 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Ingesting Requisition...</span>
                    </>
                  ) : (
                    <>
                      <span>Execute Mandate Intake</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
